"use client";

import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import ThemeToggle from "../components/ui/themeToggle";
import { cn } from "../lib/utils";
import { saveApprovedToken, isApprovedAuthenticated } from "../utils/approvedAuth";
import {
  IconBrandGithub,
  IconBrandGoogle,
  IconBrandInstagram,
  IconBrandFacebook,
  IconEye,
  IconEyeOff,
} from "@tabler/icons-react";

export default function ApprovedLogin() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });

  if (isApprovedAuthenticated()) {
    return <Navigate to="/aproved" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((current) => ({
      ...current,
      [e.target.id]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.username || !formData.password) {
      return Swal.fire({
        icon: "error",
        title: "Campos obrigatorios",
        text: "Informe usuario e senha do administrador.",
      });
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/approved/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data?.token) {
        const rawMessage =
          typeof data?.message === "string"
            ? data.message
            : "Nao foi possivel autenticar o administrador.";

        const message = getFriendlyLoginMessage(rawMessage);

        return Swal.fire({
          icon: "error",
          title: "Acesso negado",
          text: message,
        });
      }

      saveApprovedToken(data.token);

      await Swal.fire({
        icon: "success",
        title: "Login autorizado",
        text: "Acesso administrativo liberado.",
      });

      navigate("/aproved", { replace: true });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erro de conexao",
        text: "Nao foi possivel se comunicar com o servidor.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="w-full max-w-md">
        <div className="auth-card">
        <ThemeToggle />
        <div className="flex justify-center">
          <img
            src="/img/Vinkings1_logo_92x92.png"
            alt="Logo Vikings"
            className="auth-logo"
          />
        </div>
        <h1 className="auth-title">
          Acesso administrativo
        </h1>

        <form className="mt-7" onSubmit={handleSubmit}>
          <LabelInputContainer className="mb-5">
            <Label htmlFor="username">Usuário administrador *</Label>
            <Input
              id="username"
              name="username"
              placeholder="Digite o usuário"
              type="text"
              value={formData.username}
              onChange={handleChange}
              autoComplete="username"
              maxLength={100}
              disabled={loading}
            />
          </LabelInputContainer>

          <LabelInputContainer className="mb-7">
            <Label htmlFor="password">Senha *</Label>
            <PasswordField
              id="password"
              name="password"
              placeholder="Digite sua senha"
              value={formData.password}
              onChange={handleChange}
              visible={showPassword}
              onToggleVisibility={() => setShowPassword((value) => !value)}
              autoComplete="current-password"
              disabled={loading}
            />
          </LabelInputContainer>

          <button
            className="group/btn relative block h-12 w-full rounded-xl bg-gradient-to-br from-black via-neutral-900 to-neutral-600 px-4 text-base font-semibold text-white shadow-[0px_1px_0px_0px_#ffffff40_inset,0px_-1px_0px_0px_#ffffff40_inset] transition hover:from-neutral-950 hover:to-neutral-500 disabled:cursor-not-allowed disabled:opacity-70 dark:bg-zinc-800 dark:from-zinc-900 dark:to-zinc-900 dark:shadow-[0px_1px_0px_0px_#27272a_inset,0px_-1px_0px_0px_#27272a_inset] sm:h-11"
            type="submit"
            disabled={loading}
          >
            {loading ? "Entrando..." : "Acessar aprovacao ->"}
            <BottomGradient />
          </button>

          <div className="my-7 h-[1px] w-full bg-gradient-to-r from-transparent via-neutral-300 to-transparent dark:via-neutral-700 sm:my-8" />

          <div className="mt-5 flex flex-wrap justify-center gap-4 sm:gap-8">
            <OAuthButton icon={<IconBrandGithub size={24} />} provider="GitHub" />
            <OAuthButton icon={<IconBrandGoogle size={24} />} provider="Google" />
            <OAuthButton icon={<IconBrandInstagram size={24} />} provider="Instagram" />
            <OAuthButton icon={<IconBrandFacebook size={24} />} provider="Facebook" />
          </div>
        </form>
        </div>
      </div>
    </div>
  );
}

function getFriendlyLoginMessage(message: string) {
  const normalized = message.toLowerCase();

  if (
    normalized.includes("inativo") ||
    normalized.includes("pendente") ||
    normalized.includes("desativad")
  ) {
    return "A conta esta desativada ou pendente de liberacao.";
  }

  if (normalized.includes("credenciais do administrador nao configuradas")) {
    return "O acesso administrativo ainda nao foi configurado no backend.";
  }

  if (normalized.includes("usuario ou senha invalid")) {
    return "Usuario ou senha do administrador invalidos.";
  }

  return message;
}

const LabelInputContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn("flex w-full flex-col space-y-2", className)}>{children}</div>
);

const PasswordField = ({
  id,
  name,
  placeholder,
  value,
  onChange,
  visible,
  onToggleVisibility,
  autoComplete,
  disabled,
}: {
  id: string;
  name: string;
  placeholder: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  visible: boolean;
  onToggleVisibility: () => void;
  autoComplete: string;
  disabled?: boolean;
}) => (
  <div className="relative">
    <Input
      id={id}
      name={name}
      placeholder={placeholder}
      type={visible ? "text" : "password"}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      maxLength={100}
      className="h-12 rounded-xl border-slate-200 bg-slate-100/80 pr-11 text-base shadow-none focus:border-slate-300 focus:bg-white"
      disabled={disabled}
    />
    <button
      type="button"
      onClick={onToggleVisibility}
      aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
      className="absolute right-3 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-neutral-500 transition hover:bg-slate-200/70 hover:text-neutral-800 disabled:cursor-not-allowed disabled:opacity-50 dark:text-neutral-400 dark:hover:text-white"
      disabled={disabled}
    >
      {visible ? <IconEyeOff size={18} /> : <IconEye size={18} />}
    </button>
  </div>
);

const OAuthButton = ({
  icon,
  provider,
}: {
  icon: React.ReactNode;
  provider: string;
}) => (
  <div className="relative group">
    <button
      className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 shadow transition hover:bg-gray-200 dark:bg-zinc-900 dark:hover:bg-zinc-800 sm:h-11 sm:w-11"
      type="button"
      aria-label={provider}
      title={provider}
    >
      {icon}
    </button>
    <div className="absolute -bottom-8 left-1/2 -translate-x-1/2 scale-0 group-hover:scale-100 opacity-0 group-hover:opacity-100 transition bg-black text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap z-10">
      {provider}
    </div>
  </div>
);

const BottomGradient = () => (
  <>
    <span className="absolute inset-x-0 -bottom-px block h-px w-full bg-gradient-to-r from-transparent via-cyan-500 to-transparent opacity-0 transition duration-500 group-hover/btn:opacity-100" />
    <span className="absolute inset-x-10 -bottom-px mx-auto block h-1/2 w-1/2 bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-0 blur-sm transition duration-500 group-hover/btn:opacity-100" />
  </>
);
